import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { PropsWithChildren, useEffect, useState } from "react";
import { FieldPath, FieldValues } from "react-hook-form";

import { FileProps } from "../..";
import { FileAttach } from "../../ui/FileAttach";
import FilesDownload from "../../ui/FilesDownload";
import { BaseField, BaseFieldProps } from "./BaseField";

export interface FilesFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends BaseFieldProps<TFieldValues, TName> {
  placeholder?: string;
  isCTO?: boolean;
  defaultValue?: FileProps[];
  downloadFile: (file: FileProps) => (dispatch: Dispatch<AnyAction>) => void;
  vesselId: string;
  ctoId: string;
  uploadFiles: (
    files: File[],
  ) => (dispatch: Dispatch<AnyAction>) => Promise<FileProps[]>;
  backgroundColor?: string;
  border?: string;
  borderWidth?: number;
  isUpperCase?: boolean;
}

export function FilesField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  label,
  subtitle,
  labelInfo,
  defaultValue,
  rules,
  form,
  readOnly,
  downloadFile,
  vesselId,
  ctoId,
  uploadFiles,
}: PropsWithChildren<FilesFieldProps<TFieldValues, TName>>) {
  const [isLoading, setIsLoading] = useState(false);
  const { clearErrors } = form;

  useEffect(() => {
    if (!isLoading) {
      clearErrors(name);
    }
  }, [isLoading, name, clearErrors]);

  return (
    <BaseField
      form={form}
      name={name}
      label={label}
      subtitle={subtitle}
      labelInfo={labelInfo}
      defaultValue={
        defaultValue?.length
          ? defaultValue.map((item) => item.attachment_id)
          : []
      }
      rules={rules}
      fieldClassName="files-field"
      render={({ field }) =>
        readOnly && defaultValue ? (
          <FilesDownload files={defaultValue} downloadFile={downloadFile} />
        ) : (
          <FileAttach
            name={name}
            onChange={field.onChange}
            value={defaultValue ?? field.value}
            uploadFiles={uploadFiles}
            vesselId={vesselId}
            ctoId={ctoId}
            setIsLoading={setIsLoading}
            downloadFile={downloadFile}
          />
        )
      }
    />
  );
}

FilesField.defaultProps = {
  defaultValue: [],
};
